import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

type Props = { active: boolean; label?: string };
type GameProps = { onRoundComplete: () => void; label?: string; closeOnGameOver: boolean };
type PiranhaRoundHandle = { startNewSession: () => void };

function mustGet2D(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');
  return ctx;
}

const SPRITE_BASE = '/images/';

export default function LoadingGameOverlay({ active, label }: Props) {
  const [visible, setVisible]     = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const prevActiveRef             = useRef(false);
  const renderCountRef            = useRef(0);
  renderCountRef.current += 1;
  const gameRef                   = useRef<PiranhaRoundHandle | null>(null);
  const lastSessionStartAtRef     = useRef(0);
  const MIN_GUARD_MS              = 400;

  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = active;
    if (active && !prev) {
      const now = performance.now();
      if (now - lastSessionStartAtRef.current < MIN_GUARD_MS) return;
      lastSessionStartAtRef.current = now;
      setVisible(true);
      setDataReady(false);
      gameRef.current?.startNewSession();
    }
    if (!active && prev) setDataReady(true);
  }, [active]);

  const onRoundComplete = useCallback(() => {
    if (dataReady) setVisible(false);
  }, [dataReady]);

  if (!visible) return null;

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center', padding:18,
      background:'rgba(0,0,0,.58)', backdropFilter:'blur(3px)',
    }} aria-live="polite" aria-busy="true">
      <PiranhaRound ref={gameRef} onRoundComplete={onRoundComplete} label={label} closeOnGameOver={dataReady} />
    </div>
  );
}

const PiranhaRound = React.forwardRef<PiranhaRoundHandle, GameProps>(function PiranhaRound(
  { onRoundComplete, label, closeOnGameOver }, ref
) {
  const canvasRef  = useRef<HTMLCanvasElement | null>(null);
  const introOverlayRef = useRef<HTMLDivElement | null>(null);
  const introBlinkRef   = useRef<HTMLDivElement | null>(null);
  const uiScoreRef = useRef<HTMLSpanElement | null>(null);
  const uiLevelRef = useRef<HTMLSpanElement | null>(null);
  const uiSpeedRef = useRef<HTMLSpanElement | null>(null);
  const rafRef     = useRef<number | null>(null);
  const gameIdRef  = useRef(Math.random().toString(16).slice(2));

  const onRoundCompleteRef  = useRef(onRoundComplete);
  const closeOnGameOverRef  = useRef(closeOnGameOver);
  useEffect(() => { onRoundCompleteRef.current = onRoundComplete; }, [onRoundComplete]);
  useEffect(() => { closeOnGameOverRef.current = closeOnGameOver; }, [closeOnGameOver]);

  const resetFnRef = useRef<null | (() => void)>(null);
  useImperativeHandle(ref, () => ({ startNewSession: () => resetFnRef.current?.() }));

  // ── Sprite images ────────────────────────────────────────────────────────
  const spritesRef = useRef<{ closed: HTMLImageElement; neutral: HTMLImageElement; open: HTMLImageElement } | null>(null);
  const spritesReadyRef = useRef(false);
  // Fired once all three sprites are decoded — game loop picks this up
  const onSpritesReadyRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let loaded = 0;
    const onLoad = () => {
      loaded++;
      if (loaded === 3) {
        spritesReadyRef.current = true;
        onSpritesReadyRef.current?.();
      }
    };
    const onErr = (e: string | Event) => console.warn('[PiranhaPool] sprite load error', e);

    const closed  = new Image();
    const neutral = new Image();
    const open    = new Image();

    closed.onload  = onLoad; closed.onerror  = onErr; closed.src  = `${SPRITE_BASE}piranha-closed.png`;
    neutral.onload = onLoad; neutral.onerror = onErr; neutral.src = `${SPRITE_BASE}piranha-neutral.png`;
    open.onload    = onLoad; open.onerror    = onErr; open.src    = `${SPRITE_BASE}piranha-open.png`;

    spritesRef.current = { closed, neutral, open };
  }, []);

  const styles = useMemo(() => `
    .pp_frame{
      width:min(980px,96vw);
      background:linear-gradient(180deg,rgba(255,255,255,.02),rgba(255,255,255,0));
      border:1px solid #1a2a40; border-radius:14px;
      box-shadow:0 18px 55px rgba(0,0,0,.55); overflow:hidden;
    }
    .pp_topbar{
      display:flex; gap:12px; align-items:center; justify-content:space-between;
      padding:14px 16px;
      background:linear-gradient(180deg,#0f1a2a,#0c1523);
      border-bottom:1px solid #1a2a40;
    }
    .pp_brand{display:flex;align-items:center;gap:10px;user-select:none;}
    .pp_dot{
      width:12px;height:12px;border-radius:50%;background:#2ee58f;
      box-shadow:0 0 0 3px rgba(46,229,143,.12),0 0 18px rgba(46,229,143,.35);
    }
    .pp_title{font-weight:800;letter-spacing:.6px;color:#c9d6ea;font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;}
    .pp_sub{color:#7f93b3;font-size:12px;margin-top:2px;font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;}
    .pp_right{display:flex;gap:10px;align-items:center;flex-wrap:wrap;justify-content:flex-end;}
    .pp_pill{
      border:1px solid #1a2a40;background:rgba(10,17,27,.6);border-radius:999px;
      padding:7px 10px;font-size:12px;color:#7f93b3;
      display:flex;gap:8px;align-items:center;
      font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;
    }
    .pp_pill b{color:#c9d6ea;}
    .pp_accent{color:#2ee58f;}
    .pp_content{padding:16px;background:linear-gradient(180deg,rgba(255,255,255,.01),rgba(255,255,255,0));}
    .pp_screen{
      border:1px solid #1a2a40;border-radius:14px;background:#070c12;
      overflow:hidden;position:relative;box-shadow:inset 0 0 0 1px rgba(255,255,255,.03);
    }
    .pp_canvas{
      width:100%;height:auto;aspect-ratio:256/240;display:block;
      image-rendering:pixelated;image-rendering:crisp-edges;background:#070c12;
    }
    .pp_toast{
      position:absolute;left:12px;top:12px;right:12px;pointer-events:none;
      display:flex;justify-content:space-between;gap:12px;align-items:center;
    }
    .pp_toastCard{
      pointer-events:none;border:1px solid #1a2a40;background:rgba(10,17,27,.78);
      border-radius:12px;padding:10px 12px;box-shadow:0 14px 30px rgba(0,0,0,.45);
      color:#c9d6ea;font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;font-size:12px;
    }
          .pp_intro{
        position:absolute; inset:0;
        display:flex; align-items:center; justify-content:center;
        pointer-events:none;
        transition: opacity 0.4s ease;
      }
      .pp_intro.pp_introHidden{ opacity:0; }
      .pp_introCard{
        background:rgba(10,17,27,0.88);
        border:1px solid #1a2a40;
        border-radius:10px;
        padding:22px 32px 18px;
        display:flex; flex-direction:column; align-items:center; gap:6px;
        box-shadow:0 8px 32px rgba(0,0,0,.5);
        position:relative; overflow:hidden;
      }
      .pp_introAccent{
        position:absolute; top:0; left:0; right:0; height:3px;
        background:#2ee58f;
      }
      .pp_introTitle{
        color:#c9d6ea;
        font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;
        font-size:15px; font-weight:700; letter-spacing:0.3px;
        line-height:1.5; text-align:center;
      }
      .pp_introBlink{
        color:#7f93b3;
        font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;
        font-size:13px; margin-top:6px;
        animation: pp_blink 1s step-start infinite;
      }
      @keyframes pp_blink{ 0%,100%{opacity:1} 50%{opacity:0} }
      display:flex;justify-content:space-between;align-items:center;
      gap:12px;flex-wrap:wrap;padding-top:10px;color:#7f93b3;font-size:12px;
      font-family:ui-monospace,Menlo,Consolas,"Liberation Mono",monospace;
    }
  `, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = mustGet2D(canvas);

    const C = {
      bg0:'#070c12', ocean0:'#08182a', ocean1:'#0b1f35', ocean2:'#0e2742',
      grid:'rgba(39,213,255,0.07)', text:'#c9d6ea', muted:'#7f93b3',
      accent:'#2ee58f', danger:'#ff4d6d',
    } as const;

    const W = canvas.width, H = canvas.height;
    const lanes = 9, lanePad = 10;
    const laneW = Math.floor((W - lanePad * 2) / lanes);
    const laneCenterX = (i: number) => lanePad + i * laneW + Math.floor(laneW / 2);

    const oceanTop = 24, floorY = H - 18, playerY = floorY - 18;

    // ── Offscreen canvas for black→transparent masking ───────────────────
    const offscreen = document.createElement('canvas');
    const offCtx    = offscreen.getContext('2d')!;

    function drawSpriteBlackTransparent(
      img: HTMLImageElement,
      dx: number, dy: number, dw: number, dh: number,
      flipH: boolean
    ) {
      const sw = img.naturalWidth, sh = img.naturalHeight;
      if (!sw || !sh) return;
      offscreen.width = sw; offscreen.height = sh;
      offCtx.clearRect(0, 0, sw, sh);
      offCtx.drawImage(img, 0, 0);
      const id = offCtx.getImageData(0, 0, sw, sh);
      const d  = id.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i] < 18 && d[i+1] < 18 && d[i+2] < 18) d[i+3] = 0;
      }
      offCtx.putImageData(id, 0, 0);
      ctx.save();
      if (flipH) {
        ctx.translate(dx + dw, dy);
        ctx.scale(-1, 1);
        ctx.drawImage(offscreen, 0, 0, sw, sh, 0, 0, dw, dh);
      } else {
        ctx.drawImage(offscreen, 0, 0, sw, sh, dx, dy, dw, dh);
      }
      ctx.restore();
    }

    // ── Player state ─────────────────────────────────────────────────────
    const player = {
      x: laneCenterX(Math.floor(lanes / 2)),
      y: playerY,
      w: 30, h: 22,
      vx: 0, accel: 950, decel: 1300, maxSpeed: 230,
      // facing: 1 = right (default/sprites face right), -1 = left
      facing: 1 as 1 | -1,
    };

    // chompTimer counts down after catching a doc
    let chompTimer = 0;
    const CHOMP_DUR = 0.30;

    const docs: Array<{ x:number; y:number; w:number; h:number; alive:boolean; lane:number }> = [];
    let score = 0, t = 0, level = 1, spawnTimer = 0;
    let spawnInterval = 0.92, fallSpeed = 78, maxDocsOnScreen = 3;
    let running = false, gameOver = false, didNotifyRoundComplete = false, gameOverAt = 0;
    const MAX_ROUND_MS = 45_000;
    let roundStart = performance.now();
    const keys = { left: false, right: false };

    function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)); }

    function txt(x: number, y: number, s: string, color: string = C.text, align: CanvasTextAlign = 'left') {
      ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = 'top';
      ctx.font = '8px ui-monospace,Menlo,Consolas,monospace';
      ctx.fillText(s, x, y);
    }

    function drawDoc(x: number, y: number) {
      const w=10,h=12,px=Math.floor(x-w/2),py=Math.floor(y-h/2);
      ctx.fillStyle='#e9eefb'; ctx.fillRect(px,py,w,h);
      ctx.fillStyle='#cfd8f5'; ctx.fillRect(px+w-4,py,4,4);
      ctx.fillStyle='rgba(27,42,62,.85)';
      ctx.fillRect(px,py,w,1); ctx.fillRect(px,py+h-1,w,1);
      ctx.fillRect(px,py,1,h); ctx.fillRect(px+w-1,py,1,h);
      ctx.fillStyle='rgba(27,42,62,.55)';
      ctx.fillRect(px+2,py+5,w-4,1); ctx.fillRect(px+2,py+7,w-6,1);
      ctx.fillStyle=C.accent; ctx.fillRect(px+2,py+9,w-4,1);
    }

    function drawPiranha(cx: number, cy: number, facing: 1|-1, chompOpen: number) {
      const DW = 32, DH = 28;
      const dx = Math.floor(cx - DW / 2);
      const dy = Math.floor(cy - DH / 2);
      // sprites are drawn facing RIGHT natively
      // facing=1 (moving right) → no flip
      // facing=-1 (moving left) → flip horizontally
      const flipH = facing === -1;

      const sprites = spritesRef.current;
      if (!spritesReadyRef.current || !sprites) return; // invisible until ready

      // Pick frame:
      // chompOpen > 0.5 → open mouth (just caught a doc, peak chomp)
      // chompOpen > 0   → neutral (winding down)
      // else            → closed
      let img: HTMLImageElement;
      if (chompOpen > 0.5)      img = sprites.open;
      else if (chompOpen > 0)   img = sprites.neutral;
      else                      img = sprites.closed;

      drawSpriteBlackTransparent(img, dx, dy, DW, DH, flipH);

      // Bubble trail (opposite side to direction of travel)
      ctx.fillStyle = 'rgba(39,213,255,.5)';
      const bx = flipH ? dx - 4 : dx + DW + 2;
      ctx.fillRect(bx,                       dy + 10, 1, 1);
      ctx.fillRect(bx + (flipH ? -2 : 2),   dy +  6, 1, 1);
      ctx.fillRect(bx,                       dy + 14, 1, 1);
    }

    function resetGame(reason: string) {
      console.debug(`[PiranhaPool Game ${gameIdRef.current}] resetGame(${reason})`);
      docs.length = 0; score = 0; t = 0; level = 1; spawnTimer = 0; chompTimer = 0;
      spawnInterval = 0.92; fallSpeed = 78; maxDocsOnScreen = 3;
      player.x = laneCenterX(Math.floor(lanes / 2));
      player.vx = 0; player.facing = 1;
      gameOver = false; running = true; didNotifyRoundComplete = false; gameOverAt = 0;
      roundStart = performance.now();
      if (uiScoreRef.current) uiScoreRef.current.textContent = '0';
      if (uiLevelRef.current) uiLevelRef.current.textContent = '1';
      if (uiSpeedRef.current) uiSpeedRef.current.textContent = '1.0×';
    }

    resetFnRef.current = () => resetGame('startNewSession');

    function notifyCompleteOnce() {
      if (!didNotifyRoundComplete) {
        didNotifyRoundComplete = true;
        onRoundCompleteRef.current();
      }
    }

    function endGame() {
      running = false; gameOver = true; gameOverAt = performance.now();
      if (closeOnGameOverRef.current) notifyCompleteOnce();
    }

    function spawnDoc() {
      if (docs.length >= maxDocsOnScreen) return;
      const occupied = new Set<number>();
      for (const d of docs) if (d.alive && d.y < oceanTop + 60) occupied.add(d.lane);
      let lane = Math.floor(Math.random() * lanes);
      for (let i = 0; i < 8; i++) { if (!occupied.has(lane)) break; lane = Math.floor(Math.random() * lanes); }
      docs.push({ lane, x: laneCenterX(lane), y: oceanTop + 6, w: 10, h: 12, alive: true });
    }

    function updateDifficulty(dt: number) {
      t += dt;
      level = Math.max(level, 1 + Math.floor(t / 10));
      fallSpeed = clamp(78 + t * 2.2, 78, 210);
      spawnInterval = clamp(0.92 * Math.pow(0.975, t), 0.42, 0.92);
      maxDocsOnScreen = clamp(3 + Math.floor(t / 18), 3, 7);
      if (uiLevelRef.current) uiLevelRef.current.textContent = String(level);
      if (uiSpeedRef.current) uiSpeedRef.current.textContent = `${(fallSpeed / 78).toFixed(1)}×`;
    }

    function updatePlayer(dt: number) {
      const want = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
      if (want !== 0) {
        player.vx += want * player.accel * dt;
        // Only update facing when there's intentional input
        player.facing = want > 0 ? 1 : -1;
      } else {
        const s = Math.sign(player.vx), mag = Math.abs(player.vx), drop = player.decel * dt;
        player.vx = mag <= drop ? 0 : (mag - drop) * s;
        // When decelerating to a stop, keep last facing direction
      }
      player.vx = clamp(player.vx, -player.maxSpeed, player.maxSpeed);
      player.x  = clamp(player.x + player.vx * dt, lanePad + 14, W - lanePad - 14);
    }

    function collide(d: { x:number; y:number; w:number; h:number }) {
      return (
        Math.abs(player.x - d.x) < player.w / 2 + d.w / 2 - 2 &&
        Math.abs(player.y - d.y) < player.h / 2 + d.h / 2 - 2
      );
    }

    function updateDocs(dt: number) {
      if (chompTimer > 0) chompTimer = Math.max(0, chompTimer - dt);
      for (const d of docs) {
        if (!d.alive) continue;
        d.y += fallSpeed * dt;
        if (collide(d)) {
          d.alive = false; score++;
          chompTimer = CHOMP_DUR;
          if (uiScoreRef.current) uiScoreRef.current.textContent = String(score);
        }
        if (d.alive && d.y + d.h / 2 >= floorY) endGame();
      }
      for (let i = docs.length - 1; i >= 0; i--) if (!docs[i].alive) docs.splice(i, 1);
    }

    function updateSpawn(dt: number) {
      spawnTimer += dt;
      if (spawnTimer >= spawnInterval) { spawnTimer = 0; spawnDoc(); }
    }

    let waveT = 0;
    function drawBackground(dt: number) {
      waveT += dt;
      ctx.fillStyle = C.bg0; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(15,26,42,.85)'; ctx.fillRect(0, 0, W, oceanTop);
      ctx.fillStyle = 'rgba(39,213,255,.12)'; ctx.fillRect(0, oceanTop - 1, W, 1);
      for (let y = oceanTop; y < floorY; y += 4) {
        const u = (y - oceanTop) / (floorY - oceanTop);
        ctx.fillStyle = u < 0.33 ? C.ocean0 : u < 0.66 ? C.ocean1 : C.ocean2;
        ctx.fillRect(0, y, W, 4);
      }
      ctx.fillStyle = C.grid;
      for (let i = 0; i <= lanes; i++) ctx.fillRect(lanePad + i * laneW, oceanTop, 1, floorY - oceanTop);
      for (let y = oceanTop; y < floorY; y += 8) {
        const a = 0.05 + 0.04 * Math.sin(waveT * 2 + y * 0.18);
        ctx.fillStyle = `rgba(39,213,255,${a})`;
        ctx.fillRect(Math.floor(2 * Math.sin(waveT * 1.8 + y * 0.12)), y, W, 1);
      }
      ctx.fillStyle = '#0f2a24'; ctx.fillRect(0, floorY, W, H - floorY);
      txt(8, 7, `SCORE ${String(score).padStart(3,'0')}`, C.text, 'left');
      txt(W - 8, 7, `LV ${level}`, C.accent, 'right');
    }

    function hideIntro() {
      const el = introOverlayRef.current;
      if (!el) return;
      el.classList.add('pp_introHidden');
      setTimeout(() => { if (el) el.style.display = 'none'; }, 450);
    }

    function drawEntities() {
      if (!gameStarted) return; // nothing to draw before game begins
      for (const d of docs) if (d.alive) drawDoc(d.x, d.y);
      ctx.fillStyle = 'rgba(0,0,0,.25)';
      ctx.fillRect(Math.floor(player.x - 14), floorY + 1, 28, 2);
      const chompOpen = chompTimer > 0 ? chompTimer / CHOMP_DUR : 0;
      drawPiranha(player.x, player.y, player.facing, chompOpen);
    }

    function drawGameOver() {
      ctx.fillStyle = 'rgba(0,0,0,.42)'; ctx.fillRect(0, 0, W, H);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = '10px ui-monospace,Menlo,Consolas,monospace';
      ctx.fillStyle = C.danger; ctx.fillText('GAME OVER', W/2, H/2 - 10);
      ctx.font = '8px ui-monospace,Menlo,Consolas,monospace';
      ctx.fillStyle = C.text;  ctx.fillText(`Final Score: ${score}`, W/2, H/2 + 6);
      ctx.fillStyle = C.muted; ctx.fillText(closeOnGameOverRef.current ? 'Returning…' : 'Restarting…', W/2, H/2 + 20);
    }

    function onKey(e: KeyboardEvent, down: boolean) {
      const k = e.key.toLowerCase();
      if (k === 'arrowleft'  || k === 'a') keys.left  = down;
      if (k === 'arrowright' || k === 'd') keys.right = down;
    }
    const keydown = (e: KeyboardEvent) => onKey(e, true);
    const keyup   = (e: KeyboardEvent) => onKey(e, false);
    window.addEventListener('keydown', keydown);
    window.addEventListener('keyup',   keyup);

    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      drawBackground(dt);
      if (running && !gameOver) {
        updateDifficulty(dt); updatePlayer(dt); updateSpawn(dt); updateDocs(dt);
      }
      drawEntities();
      if (gameOver) {
        drawGameOver();
        if (!closeOnGameOverRef.current && gameOverAt && now - gameOverAt > 650) resetGame('autoRestart');
      }
      if (!gameOver && now - roundStart > MAX_ROUND_MS) endGame();
      rafRef.current = requestAnimationFrame(loop);
    };

    // ── Pre-game intro state ─────────────────────────────────────────────
    // Show instruction screen for MIN_INTRO_MS, then wait for sprites.
    // Game only starts once BOTH conditions are met.
    const MIN_INTRO_MS = 3000;
    let introComplete = false;
    let spritesComplete = spritesReadyRef.current;
    let gameStarted = false;

    function tryStartGame() {
      if (gameStarted || !introComplete || !spritesComplete) return;
      gameStarted = true;
      hideIntro();
      resetGame('mount');
    }

    setTimeout(() => {
      introComplete = true;
      tryStartGame();
    }, MIN_INTRO_MS);

    if (spritesReadyRef.current) {
      spritesComplete = true;
    } else {
      onSpritesReadyRef.current = () => {
        spritesComplete = true;
        tryStartGame();
      };
    }

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('keydown', keydown);
      window.removeEventListener('keyup',   keyup);
    };
  }, []);

  return (
    <div className="pp_frame">
      <style>{styles}</style>
      <div className="pp_topbar">
        <div className="pp_brand">
          <div className="pp_dot" />
          <div>
            <div className="pp_title">PIRANHA POOL</div>
            <div className="pp_sub">Catch falling documents • don't let one hit the floor</div>
          </div>
        </div>
        <div className="pp_right">
          <div className="pp_pill"><span>Score</span><b ref={uiScoreRef}>0</b></div>
          <div className="pp_pill"><span>Level</span><b ref={uiLevelRef}>1</b></div>
          <div className="pp_pill"><span>Speed</span><b className="pp_accent" ref={uiSpeedRef}>1.0×</b></div>
        </div>
      </div>
      <div className="pp_content">
        <div className="pp_screen">
          <div className="pp_toast">
            <div className="pp_toastCard">{label || 'Loading…'}</div>
            <div className="pp_toastCard">
              {closeOnGameOver ? 'Data ready • closes on GAME OVER' : 'Play while we load • keeps going'}
            </div>
          </div>
          <canvas ref={canvasRef} className="pp_canvas" width={256} height={240} />
          {/* Intro overlay – rendered in DOM so text is crisp at full resolution */}
          <div ref={introOverlayRef} className="pp_intro">
            <div className="pp_introCard">
              <div className="pp_introAccent" />
              <div className="pp_introTitle">Use the Left &amp; Right Arrows</div>
              <div className="pp_introTitle">to Chomp the Docs!</div>
              <div ref={introBlinkRef} className="pp_introBlink">◀&nbsp;&nbsp;CHOMP&nbsp;&nbsp;▶</div>
            </div>
          </div>
        </div>
        <div className="pp_footerRow">
          <div>Move: ◀ ▶ or A D</div>
          <div>Tip: survival comes from lane changes, not speed.</div>
        </div>
      </div>
    </div>
  );
});

PiranhaRound.displayName = 'PiranhaRound';